import React, { MouseEventHandler } from 'react'
import { runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react-lite'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import { Box, CircularProgress, FormHelperText, Grid, Typography } from '@material-ui/core'

import { StoresFormBaseProps, StoresFormDirect } from './StoresFormDirect'
import { WarehouseKeyProps } from './WarehousePicker'
import { RelationTypeRadio, RelationTypeRadioProps } from './RelationTypeRadio'
import { MaximumProductStocksForm } from './MaximumProductStocksForm'
import { MinimumProductStocksForm } from './MinimumProductStocksForm'
import { StoresFormSumFromMoySklad } from './StoresFormSumFromMoySklad'
import { StoresFormAllToOneMoySklad } from './StoresFormAllToOneMoySklad'
import { StoresFormSplitFromMoySklad } from './StoresFormSplitFromMoySklad'
import {
  StoresFormSplitMoySkladByDimensions,
  StoresFormSplitMoySkladByDimensionsProps
} from './StoresFormSplitMoySkladByDimensions'

import { useMessages } from '../hooks/snackbarHooks'
import { useApi, useLogic } from '../hooks/storeHook'
import { LoadState, useLoadState } from '../hooks/loadStateHook'
import { useBoolState, useLazyEffect } from '../hooks/commonHooks'

import { UnprocessedValueError } from '../common/tsUtils'
import { useLocalObservableOptional } from '../common/mobxUtils'
import { AccountProps, StoreAccountModel } from '../types/accountProps'

import { EditStoreModel } from '../server/mpsklad_core/Models/EditStoreModel'
import { StoreModelBase } from '../server/mpsklad_core/Models/StoreModelBase'
import { WarehouseModel } from '../server/mpsklad_core/Models/WarehouseModel'
import { EditStoresModel } from '../server/mpsklad_core/Models/EditStoresModel'
import { IntegrationType } from '../server/mpsklad_core/Entity/IntegrationType'
import { MoySkladStoreModel } from '../server/mpsklad_core/Models/MoySkladStoreModel'
import { CreateMsStoresModel } from '../server/mpsklad_core/Models/CreateMsStoresModel'
import { StoreRelationType } from '../server/mpsklad_core/Entity/Base/StoreRelationType'
import { integrationTypeNames } from '../types/integrationTypeUtils'

export type AccountStoresFormProps<
  TAccount extends StoreAccountModel, TStore extends StoreModelBase,
  TEditStore extends EditStoreModel, TWarehouse extends WarehouseModel> =
  WarehouseKeyProps<TWarehouse>
  & AccountProps<TAccount>
  & Pick<StoresFormBaseProps<TStore, TEditStore, TWarehouse>, 'initStoreCargoType'>
  & Pick<StoresFormSplitMoySkladByDimensionsProps<TStore, TEditStore, TWarehouse>,
  'updateStoreCargoType' | 'filterWhByCargoType'>
  & {
  hint?: string

  formatWarehouseNameHint?: (warehouse: TWarehouse) => string

  loadWarehouses: (accountId: number) => Promise<TWarehouse[]>

  loadStores: (accountId: number) => Promise<TStore[]>

  loadingHelpText?: string

  editStores: (model: EditStoresModel<TEditStore>) => Promise<void>
}

export type AccountStoresServerData<TStore extends StoreModelBase, TWarehouse extends WarehouseModel> = {
  stores: TStore[]

  warehouses: TWarehouse[]

  msStores: MoySkladStoreModel[]
}

export const AccountStoresForm =
  observer(
    <TAccount extends StoreAccountModel, TStore extends StoreModelBase, TEditStore extends EditStoreModel, TWarehouse extends WarehouseModel>
    ({
       account, hint, initStoreCargoType,
       loadWarehouses, loadStores, loadingHelpText, editStores, updateStoreCargoType,
       whKeySelector, storeWhKeySelector, filterWhByCargoType,
       formatWarehouseNameHint
     }: AccountStoresFormProps<TAccount, TStore, TEditStore, TWarehouse>) => {
      const integrationTypeName = integrationTypeNames[account.integrationType]

      const classes = useStyles()
      const {showSuccess, showError} = useMessages()

      const api = useApi()
      const {showDialog} = useLogic()

      const {loadState, setLoading, setSuccess, setError} = useLoadState(LoadState.Loading)

      const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()

      const serverData =
        useLocalObservable(() => ({
          stores: Array.of<TStore>(),
          warehouses: Array.of<TWarehouse>(),
          msStores: Array.of<MoySkladStoreModel>()
        } satisfies AccountStoresServerData<TStore, TWarehouse>))

      const formData = useLocalObservableOptional<EditStoresModel<TEditStore>>()

      const importWarehousesToMs =
        async (warehouses: CreateMsStoresModel) => {
          await api.userSync.importWarehousesToMs(warehouses)
          await loadMsStores()
        }

      const formatWarehouseName =
        (warehouse: TWarehouse): string =>
          `${warehouse.id ? `${warehouse.id} ` : ''}${warehouse.name}`

      const formatWarehouseDisplayName =
        (warehouse: TWarehouse) =>
          `${formatWarehouseName(warehouse)}${formatWarehouseNameHint ? ` ${formatWarehouseNameHint(warehouse)}` : ''}`

      const loadMsStores =
        async () => serverData.msStores = await api.userSync.getMoySkladStores()

      const initFormData = () =>
        formData.dataOrNull = {
          accountId: account.id,
          relationType: account.storeRelationType,
          ordersMsStoreId: account.ordersMsStoreId,
          stores: serverData.stores.map(store => {
            const editStoreBase = {
              storeId: store.id,
              warehouseId: store.warehouseId,
              warehouseName: store.name,
              stocksSplitPercentage: store.stocksSplitPercentage,
              msStoreIds: store.msStoreIds
            } satisfies EditStoreModel

            // Priorituze cargo type from marketplace API
            const warehouse = serverData.warehouses.find(wh => whKeySelector(wh) === storeWhKeySelector(editStoreBase))

            return initStoreCargoType(warehouse ?? store, editStoreBase)
          })
        }

      const onChangeMaximumProductStocks =
        (maximumProductStocks: number | undefined) =>
          formData.data.maximumProductStocks = maximumProductStocks

      const onChangeMinimumProductStocks =
        (minimumProductStocks: number | undefined) =>
          formData.data.minimumProductStocks = minimumProductStocks

      const onLoad =
        async (onBeforeInit?: () => void) => {
          setLoading()

          try {
            serverData.stores = await loadStores(account.id)
            serverData.warehouses = await loadWarehouses(account.id)

            await loadMsStores()

            runInAction(() => {
              onBeforeInit?.()
              initFormData()
            })

            if (serverData.stores.length === 0 && serverData.msStores.length === 0) {
              showError(`Не найдены склады ни ${integrationTypeName}, ни МоегоСклада!`)
              setError()
              return
            }

            setSuccess()
          } catch (e) {
            console.error('Failed to load', e)
            setError()
          }
        }

      const onImport =
        async (warehouse: TWarehouse) => {
          setSubmitting()

          try {
            if (warehouse) {
              await importWarehousesToMs({warehousesNames: [formatWarehouseName(warehouse)]})
              showSuccess(`Склад создан в МоёмСкладе!`)
            }
          } catch (e) {
            console.error('Failed to import warehouse to MS', e)
          } finally {
            setSubmitted()
          }
        }

      const onImportAll =
        async () => {
          setSubmitting()

          try {
            const warehousesNames = serverData.warehouses.map(warehouse => formatWarehouseName(warehouse))
            await importWarehousesToMs({warehousesNames})
            showSuccess(`Склады созданы в МоёмСкладе!`)
          } catch (e) {
            console.error('Failed to import all warehouses to MS', e)
            setError()
            return
          } finally {
            setSubmitted()
          }

          await onLoad()
        }

      const onCancel =
        async () => {
          if (await showDialog('Матчинг складов будет сброшен к предыдущему состоянию.', {
            title: 'Отменить изменения?',
            acceptButton: 'Отменить изменения',
            declineButton: 'Продолжить матчинг'
          })) {
            initFormData()
          }
        }

      const onSubmit =
        async () => {
          // Omit unmatched stores
          formData.data.stores = formData.data.stores.filter(_ => _.msStoreIds.length > 0)

          setSubmitting()

          try {
            await editStores(formData.data)
            showSuccess('Склады сохранены!')

            await onLoad(() => {
              // Update account props atomically, otherwise validation throws errors
              account.storeRelationType = formData.data.relationType
              account.ordersMsStoreId = formData.data.ordersMsStoreId
              account.maximumProductStocks = formData.data.maximumProductStocks
              account.minimumProductStocks = formData.data.minimumProductStocks
            })
          } catch (e) {
            console.error('Failed to save stores', e)
          } finally {
            setSubmitted()
          }
        }

      const onRelationTypeChange =
        async (newValue: StoreRelationType) => {
          const {data} = formData

          if (newValue === data.relationType) {
            return
          }

          switch (newValue) {
            case StoreRelationType.Direct: {
              if ((data.stores.length > 1 || data.stores.flatMap(_ => _.msStoreIds).length > 1)
                  && !await showDialog(
                  `Необходимо выбрать склады ${integrationTypeName} и МоегоСклада без повторений!`)) {
                return
              }

              runInAction(() => {
                const extraStores: TEditStore[] = []

                for (const formStore of data.stores) {
                  if (formStore.msStoreIds.length > 1) {
                    extraStores.push(
                      ...formStore.msStoreIds
                                  .slice(1)
                                  .map(msStoreId => ({
                                    ...formStore,
                                    storeId: undefined,
                                    msStoreIds: [msStoreId]
                                  } satisfies TEditStore)))

                    formStore.msStoreIds = [formStore.msStoreIds[0]]
                  }
                }

                data.stores.push(...extraStores)
                data.ordersMsStoreId = undefined
                data.relationType = newValue
              })
              return
            }

            case StoreRelationType.SumFromMoySklad: {
              if (data.stores.length > 1
                  && !await showDialog(`Матчинг складов ${integrationTypeName} будет сброшен!`)) {
                return
              }

              runInAction(() => {
                if (data.stores.length > 0) {
                  const msStoreIds = data.stores.flatMap(_ => _.msStoreIds)

                  data.stores = [{
                    ...data.stores[0],
                    msStoreIds: msStoreIds.length > 0 ? [msStoreIds[0]] : []
                  }]
                } else {
                  data.stores = []
                }

                data.ordersMsStoreId = serverData.msStores[0]?.id
                data.relationType = newValue
              })
              return
            }

            case StoreRelationType.SplitFromMoySklad: {
              const msStoreIds = data.stores.flatMap(_ => _.msStoreIds)

              if (msStoreIds.length > 1 && !await showDialog('Матчинг складов МоегоСклада будет сброшен!')) {
                return
              }

              runInAction(() => {
                if (msStoreIds.length > 0) {
                  for (const formStore of data.stores) {
                    formStore.msStoreIds = [msStoreIds[0]]
                    formStore.stocksSplitPercentage = 0
                  }
                }

                data.ordersMsStoreId = undefined
                data.relationType = newValue
              })
              return
            }

            case StoreRelationType.AllToOneMoySklad: {
              if (account.isDeliveryFromSellerWarehouse) {
                throw new Error(`Bad relation type for delivery type!`)
              }

              const msStoreIds = data.stores.flatMap(_ => _.msStoreIds)

              if (msStoreIds.length > 1 && !await showDialog('Матчинг складов МоегоСклада будет сброшен!')) {
                return
              }

              runInAction(() => {
                if (msStoreIds.length > 0) {
                  for (const formStore of data.stores) {
                    formStore.msStoreIds = [msStoreIds[0]]
                    formStore.stocksSplitPercentage = undefined
                  }
                }

                data.ordersMsStoreId = undefined
                data.relationType = newValue
              })
              return
            }

            case StoreRelationType.SplitMoySkladByDimensions: {
              if (!account.isDeliveryFromSellerWarehouse) {
                throw new Error(`Bad relation type for delivery type!`)
              }

              const msStoreIds = data.stores.flatMap(_ => _.msStoreIds)

              if (msStoreIds.length > 1 && !await showDialog('Матчинг складов МоегоСклада будет сброшен!')) {
                return
              }

              runInAction(() => {
                if (msStoreIds.length > 0) {
                  for (const formStore of data.stores) {
                    formStore.msStoreIds = [msStoreIds[0]]
                    formStore.stocksSplitPercentage = undefined
                  }
                }

                data.ordersMsStoreId = undefined
                data.relationType = newValue
              })
              return
            }

            default:
              throw new UnprocessedValueError('StoreRelationType', newValue)
          }
        }

      const onRelationTypeClick =
        (newValue: StoreRelationType): MouseEventHandler<unknown> =>
          async e => {
            e.preventDefault()
            e.stopPropagation()

            await onRelationTypeChange(newValue)
          }

      useLazyEffect(onLoad)

      const commonRadioProps = {
        hint,
        isSubmitting,
        onRelationTypeClick
      } satisfies Omit<RelationTypeRadioProps<TWarehouse>, 'option' | 'formData' | 'renderStoreForm'>

      const commonStoreFormProps = {
        storeType: account.integrationType,
        disabled: isSubmitting,
        serverData,
        initStoreCargoType,
        whKeySelector,
        storeWhKeySelector,
        formatWarehouseDisplayName
      } satisfies Omit<StoresFormBaseProps<TStore, TEditStore, TWarehouse>, 'formData'>

      // TODO: Wrap into a form? Layout will probably be affected though.
      return (
        <Box className={classes.pageContainer}>
          <Typography className={classes.header}>
            Настройка складов
          </Typography>

          {
            (loadState === LoadState.Initial || loadState === LoadState.Error) &&
            <button className={clsx(classes.saveButton, 'default-button')} onClick={() => onLoad()}>
              <p>Загрузить</p>
            </button>
          }

          {
            loadState === LoadState.Loading &&
            <>
              <CircularProgress size={25}/>

              {
                !!loadingHelpText &&
                <FormHelperText>
                  {loadingHelpText}
                </FormHelperText>
              }
            </>
          }

          {
            loadState === LoadState.Success &&
            <Grid container direction="column" spacing={4}>
              <RelationTypeRadio
                {...commonRadioProps}
                option={{
                  value: StoreRelationType.Direct,
                  label: 'Базовое сопоставление',
                  hint: 'Сопоставляем склады один к одному.'
                }}
                formData={formData.data}
                renderStoreForm={() =>
                  <StoresFormDirect
                    {...commonStoreFormProps}
                    formData={formData.data}
                    onImport={onImport}
                    onImportAll={onImportAll}
                  />
                }
              />

              {
                // If this option is already selected, show even for wrong delivery types
                (account.storeRelationType === StoreRelationType.SumFromMoySklad
                 || account.isDeliveryFromSellerWarehouse) &&
                <RelationTypeRadio
                  {...commonRadioProps}
                  option={{
                    value: StoreRelationType.SumFromMoySklad,
                    label: 'Суммирование',
                    hint: 'Остатки суммируются с нескольких складов МоегоСклада на один склад маркетплейса.'
                  }}
                  formData={formData.data}
                  renderStoreForm={() =>
                    <StoresFormSumFromMoySklad {...commonStoreFormProps} formData={formData.data}/>}
                />
              }

              {
                account.isDeliveryFromSellerWarehouse
                && (account.integrationType === IntegrationType.Ozon
                    || account.integrationType === IntegrationType.Wildberries) &&
                <RelationTypeRadio
                  {...commonRadioProps}
                  option={{
                    value: StoreRelationType.SplitMoySkladByDimensions,
                    label: 'Распределение по габаритности',
                    hint: 'Остатки из одного склада МоегоСклада отправляются в один из складов маркетплейса по совпадению габаритности товара и склада.'
                  }}
                  formData={formData.data}
                  renderStoreForm={() =>
                    <StoresFormSplitMoySkladByDimensions
                      {...commonStoreFormProps}
                      formData={formData.data}
                      filterWhByCargoType={filterWhByCargoType}
                      updateStoreCargoType={updateStoreCargoType}
                    />}
                />
              }

              {
                // Show this option only if it is already selected
                account.storeRelationType === StoreRelationType.SplitFromMoySklad &&
                <RelationTypeRadio
                  {...commonRadioProps}
                  option={{
                    value: StoreRelationType.SplitFromMoySklad,
                    label: 'Разделение',
                    hint: 'Остатки разделяются по нескольким складам маркетплейса. Разделение устанавливается в процентах.'
                  }}
                  formData={formData.data}
                  renderStoreForm={() =>
                    <StoresFormSplitFromMoySklad {...commonStoreFormProps} formData={formData.data}/>}
                />
              }

              {
                !account.isDeliveryFromSellerWarehouse &&
                <RelationTypeRadio
                  {...commonRadioProps}
                  option={{
                    value: StoreRelationType.AllToOneMoySklad,
                    label: 'К одному складу МоегоСклада',
                    hint: 'Все склады маркетплейса сопоставляются к одному складу МоегоСклада. Остатки МоегоСклада не отправляются в маркетплейсы.'
                  }}
                  formData={formData.data}
                  renderStoreForm={() =>
                    <StoresFormAllToOneMoySklad {...commonStoreFormProps} formData={formData.data}/>}
                />
              }

              <MaximumProductStocksForm account={account} onChange={onChangeMaximumProductStocks}/>

              <MinimumProductStocksForm account={account} onChange={onChangeMinimumProductStocks}/>

              <Grid item container xs={12} spacing={1} className={classes.buttonsItem}>
                <Grid item xs={'auto'}>
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className={clsx(classes.cancelButton, 'default-button')}
                    onClick={onCancel}>
                    <p>Отменить</p>
                  </button>
                </Grid>

                <Grid item xs={'auto'}>
                  <button
                    disabled={isSubmitting}
                    className={clsx(classes.saveButton, 'default-button')}
                    onClick={onSubmit}>
                    <p>Сохранить</p>
                  </button>
                </Grid>
              </Grid>
            </Grid>
          }
        </Box>
      )
    })

const useStyles = makeStyles(
  ({palette: {mpSkladPrimary, mpSkladSecondary}}) =>
    ({
      pageContainer: {
        margin: '0 40px 40px 40px'
      },
      dash: {
        color: mpSkladPrimary.blue,
        marginRight: 3
      },
      header: {
        fontSize: '18px',
        fontWeight: 600,
        lineHeight: '21.6px',
        textAlign: 'left',
        margin: '10px 10px 30px 0',
        textTransform: 'uppercase'
      },
      buttonsItem: {
        marginTop: 8,
        '& button': {
          marginTop: 0,
          marginBottom: 0
        }
      },
      saveButton: {
        width: 160,
        height: 40,
        color: mpSkladPrimary.white,
        alignItems: 'center'
      },
      cancelButton: {
        width: 160,
        height: 40,
        color: mpSkladPrimary.white,
        alignItems: 'center',
        backgroundColor: mpSkladSecondary.lightGray,
        '&:hover': {
          backgroundColor: '#BCBCBC'
        }
      }
    }))